import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { faLock } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { PermissionRoles } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { useHasPermissions } from "@/utils/useHasPermissions";

interface EmailWorkspaceRowsProps {
	showBounceRepair: boolean;
	showSuppression: boolean;
	/** Bounce repair as the workspace has it; `undefined` while Company Settings could not be read. */
	bounceRepair: { enabled: boolean; reviewOnCompanyChange: boolean } | undefined;
}

const CONTACTS_SETTINGS_PATH = `${ROUTE_CONFIG.COMPANY}?tab=CONTACTS`;

/**
 * The read-only, workspace-wide rows of a list's Email deliverability section (PREC-4261). Each
 * row shows only once its slice has shipped (bounce repair 8/14, suppression 6/14). The link to
 * Company Settings is offered only to a user who may open it; everyone else reads the same rows
 * with a note that only Company Settings users can change them.
 */
export const EmailWorkspaceRows: FunctionComponent<EmailWorkspaceRowsProps> = ({
	showBounceRepair,
	showSuppression,
	bounceRepair,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`contacts.settings.listDeliverability.${key}`);
	const { hasPermissions } = useHasPermissions();

	if (!showBounceRepair && !showSuppression) {
		return null;
	}

	const bounceRepairSummary = !bounceRepair
		? null
		: !bounceRepair.enabled
			? ts("bounceRepairOff")
			: bounceRepair.reviewOnCompanyChange
				? ts("bounceRepairOnWithReview")
				: ts("bounceRepairOn");

	return (
		<div className="flex flex-col gap-2" data-testid="list-email-workspace-rows">
			<span className="text-xs text-text-meta">{ts("workspaceWideHeading")}</span>
			<div className="rounded-lg border border-border-light">
				{showBounceRepair && (
					<WorkspaceRow
						summary={bounceRepairSummary}
						testId="list-bounce-repair-row"
						title={ts("bounceRepairTitle")}
					/>
				)}
				{showSuppression && (
					<WorkspaceRow
						summary={ts("suppressionSummary")}
						testId="list-suppression-row"
						title={ts("suppressionTitle")}
					/>
				)}
			</div>
			<div className="flex justify-end">
				{hasPermissions([PermissionRoles.COMPANY]) ? (
					<Link
						className="cursor-pointer text-xs font-medium text-blue-action hover:underline"
						data-testid="email-deliverability-company-settings-link"
						to={CONTACTS_SETTINGS_PATH}
					>
						{ts("changeInCompanySettings")}
					</Link>
				) : (
					<span
						className="flex items-center gap-1 text-xs text-text-meta"
						data-testid="email-deliverability-company-settings-note"
					>
						<Icon className="w-[10px] h-auto" icon={faLock} />
						{ts("companySettingsAccessOnly")}
					</span>
				)}
			</div>
		</div>
	);
};

interface WorkspaceRowProps {
	testId: string;
	title: string;
	summary: string | null;
}

const WorkspaceRow: FunctionComponent<WorkspaceRowProps> = ({ testId, title, summary }) => {
	const { t } = useTranslation();

	return (
		<div
			className="flex items-center justify-between gap-4 border-b border-border-light px-4 py-3 last:border-b-0"
			data-testid={testId}
		>
			<div className="flex items-center gap-4">
				<span className="text-sm font-medium text-text">{title}</span>
				{summary && <span className="text-sm text-text-meta">{summary}</span>}
			</div>
			<span className="rounded bg-background-light px-1.5 py-0.5 text-xs text-text-meta">
				{t("contacts.settings.workspaceWide")}
			</span>
		</div>
	);
};
